import { createSelector } from '@reduxjs/toolkit';
import {
  currentEmployeeUuid,
  employeesShortNamesAndI,
  type NameEntry,
  riskAssessmentsNotDeleted,
  tasksNotDeleted,
} from 'src/store/selectors';
import type { FunctionViewModel, RiskAssessmentViewModel, TaskViewModel } from '@trinnvis/dabih-api-client';
import type {
  EmergencyContact,
  EmergencyFunctionWithTasks,
  EmergencyLinkContact,
  EmergencyMeasuresCard,
} from 'src/outskirts/account/emergency/emergency-view-model';
import { functionsByRank } from 'src/store/selectors/functions/functions-by-rank';
import {
  getContactsWithContactInfo,
  getEmployeesWithContactInfo,
  getPartnersWithContactInfo,
} from 'src/store/selectors/emergency/emergency-contacts';
import { sortBy } from 'lodash';
import { docsForLinkingById } from 'src/store/selectors/linking/docsForLinkingById';
import { convertInternalLinks } from 'src/models/internal-linking';

function getContactsFromLinks(htmlString: string): EmergencyLinkContact[] {
  const linkContacts: EmergencyLinkContact[] = [];
  const tagRegex = /<a\s+([^>]+)>/gi;
  const doctypeRegex = /data-internal-link-doctype=["']([^"']+)["']/;
  const idRegex = /data-internal-link-id=["']([^"']+)["']/;
  let match: any[] | null;
  while ((match = tagRegex.exec(htmlString)) !== null) {
    const attributes = match[1];
    const doctypeMatch = attributes.match(doctypeRegex);
    const idMatch = attributes.match(idRegex);
    if (
      doctypeMatch &&
      idMatch &&
      (doctypeMatch[1] === 'employees' || doctypeMatch[1] === 'partners' || doctypeMatch[1] === 'contacts')
    ) {
      linkContacts.push({
        docType: doctypeMatch[1],
        uuid: idMatch[1],
      });
    }
  }
  return linkContacts;
}

function getContentContacts(
  cardTasks: TaskViewModel[],
  employees: EmergencyContact[],
  partners: EmergencyContact[],
  contacts: EmergencyContact[],
): EmergencyContact[] {
  const result: EmergencyContact[] = [];
  const cardContacts: EmergencyLinkContact[] = [];
  cardTasks.forEach((task) => {
    if (task.procedures) {
      const taskContacts = getContactsFromLinks(task.procedures);
      if (taskContacts.length) {
        cardContacts.push(...taskContacts);
      }
    }
  });
  const seenIds = new Set();
  const uniqueCardContacts = cardContacts.filter((item) => {
    if (seenIds.has(item.uuid)) {
      return false;
    }
    seenIds.add(item.uuid);
    return true;
  });
  uniqueCardContacts.forEach((item) => {
    let list: EmergencyContact[] = [];
    if (item.docType === 'employees') {
      list = employees;
    }
    if (item.docType === 'partners') {
      list = partners;
    }
    if (item.docType === 'contacts') {
      list = contacts;
    }
    const found = list.find((e) => e.uuid === item.uuid);
    if (found) {
      result.push(found);
    }
  });
  return sortBy(result, (r) => r.name);
}

function getAssignedEmployeesWithContactInfo(functions: EmergencyFunctionWithTasks[], employees: EmergencyContact[]) {
  const uuids: string[] = [];
  functions.forEach((f) => {
    f.employees.forEach((e) => {
      uuids.push(e.uuid);
    });
  });
  return employees.filter((e) => uuids.includes(e.uuid));
}

export const emergencyMeasuresCards = createSelector(
  riskAssessmentsNotDeleted,
  functionsByRank,
  tasksNotDeleted,
  employeesShortNamesAndI,
  currentEmployeeUuid,
  getEmployeesWithContactInfo,
  getPartnersWithContactInfo,
  getContactsWithContactInfo,
  docsForLinkingById,
  function (
    riskAssessments: RiskAssessmentViewModel[],
    functions: FunctionViewModel[],
    tasks: TaskViewModel[],
    employeesShortNames: NameEntry[],
    userUuid: string | undefined,
    employees: EmergencyContact[],
    partners: EmergencyContact[],
    contacts: EmergencyContact[],
    docsIndex,
  ): EmergencyMeasuresCard[] {
    if (userUuid) {
      return riskAssessments
        .filter((r) => {
          return r.measuresCardPublished;
        })
        .map((r) => {
          const card: EmergencyMeasuresCard = {
            uuid: r.uuid,
            name: r.name ?? '',
            causeCategories: r.causeCategories ?? [],
            userFunctions: [],
            otherFunctions: [],
            contactsInContent: [],
            assignedEmployees: [],
          };
          if (r.relatedTasks) {
            const cardTaskUuids: string[] = r.relatedTasks;
            const cardTasks: TaskViewModel[] = [];
            cardTaskUuids.forEach((uuid) => {
              const cardTask = tasks.find((t) => t.uuid === uuid);
              if (cardTask) {
                cardTasks.push(cardTask);
              }
            });
            if (cardTasks.length) {
              card.contactsInContent = getContentContacts(cardTasks, employees, partners, contacts);
            }
            functions.forEach((f) => {
              const cardFunction: EmergencyFunctionWithTasks = {
                uuid: f.uuid,
                name: f.name ?? '',
                employees: [],
                tasks: [],
              };
              f.employees.forEach((uuid) => {
                const employee = employeesShortNames.find((e) => e.uuid === uuid);
                if (employee && uuid !== userUuid) {
                  cardFunction.employees.push(employee);
                }
              });
              const functionTasks: TaskViewModel[] = cardTasks.filter((t) => t.functionUuid === f.uuid);
              if (functionTasks.length) {
                functionTasks.forEach((ft) => {
                  cardFunction.tasks.push({
                    uuid: ft.uuid,
                    name: ft.name ?? '',
                    content: convertInternalLinks(ft.procedures ?? '', docsIndex),
                  });
                });
                if (f.employees.includes(userUuid)) {
                  card.userFunctions.push(cardFunction);
                } else {
                  card.otherFunctions.push(cardFunction);
                }
              }
            });
          }
          card.assignedEmployees = getAssignedEmployeesWithContactInfo(
            [...card.userFunctions, ...card.otherFunctions],
            employees,
          );
          return card;
        });
    }
    return [];
  },
);
